// ============================================================================
// PRICE FORMATTING
// ============================================================================

export function formatPrice(
  price: number,
  currency: string = "CZK",
  locale: string = "cs-CZ"
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

export function formatPriceCompact(price: number): string {
  if (price >= 1000000) {
    return `${(price / 1000000).toFixed(1)} mil.`;
  }
  if (price >= 1000) {
    return `${Math.round(price / 1000)} tis.`;
  }
  return price.toString();
}

// ============================================================================
// DATE FORMATTING
// ============================================================================

export function formatDate(
  date: Date | string,
  options?: Intl.DateTimeFormatOptions,
  locale: string = "cs-CZ"
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString(locale, {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    ...options,
  });
}

export function formatDateRange(
  startDate: Date | string,
  endDate: Date | string,
  locale: string = "cs-CZ"
): string {
  const start = typeof startDate === "string" ? new Date(startDate) : startDate;
  const end = typeof endDate === "string" ? new Date(endDate) : endDate;

  const startStr = start.toLocaleDateString(locale, {
    day: "numeric",
    month: "numeric",
  });
  const endStr = end.toLocaleDateString(locale, {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });

  return `${startStr} - ${endStr}`;
}

export function formatRelativeDate(
  date: Date | string,
  locale: string = "cs"
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "dnes";
  if (diffDays === 1) return "zítra";
  if (diffDays === -1) return "včera";
  if (diffDays > 0 && diffDays <= 7) return `za ${diffDays} ${pluralize(diffDays, "den", "dny", "dní")}`;
  if (diffDays < 0 && diffDays >= -7) return `před ${Math.abs(diffDays)} ${pluralize(Math.abs(diffDays), "dnem", "dny", "dny")}`;

  return formatDate(d, undefined, locale);
}

export function daysUntil(date: Date | string): number {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

// ============================================================================
// STRING UTILITIES
// ============================================================================

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-"); // Remove consecutive hyphens
}

export function truncate(text: string, maxLength: number = 100): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).replace(/\s+\S*$/, "") + "...";
}

export function pluralize(
  count: number,
  singular: string,
  few: string,
  many: string
): string {
  if (count === 1) return singular;
  if (count >= 2 && count <= 4) return few;
  return many;
}

export function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// ============================================================================
// TRIP UTILITIES
// ============================================================================

export function formatDuration(days: number): string {
  if (days === 1) return "1 den";
  if (days >= 2 && days <= 4) return `${days} dny`;
  return `${days} dní`;
}

export function formatGroupSize(min: number, max: number): string {
  if (min === max) return `${min} osob`;
  return `${min}-${max} osob`;
}

export function calculateDiscount(
  originalPrice: number,
  currentPrice: number
): number {
  if (!originalPrice || originalPrice <= currentPrice) return 0;
  return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
}

export function getAvailableSpots(capacity: number, booked: number): number {
  return Math.max(0, capacity - booked);
}

export function isFewSpots(capacity: number, booked: number): boolean {
  const available = getAvailableSpots(capacity, booked);
  return available > 0 && available <= 3;
}

// ============================================================================
// ORDER UTILITIES
// ============================================================================

export function generateOrderNumber(prefix: string = "GO2"): string {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${year}-${random}`;
}

export function generateVoucherCode(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // Exclude confusing characters
  let code = "GO2-GIFT-";
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

// ============================================================================
// URL UTILITIES
// ============================================================================

export function getYoutubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export function getYoutubeThumbnail(
  url: string,
  quality: "default" | "medium" | "high" | "maxres" = "high"
): string | null {
  const videoId = getYoutubeVideoId(url);
  if (!videoId) return null;

  const qualityMap = {
    default: "default",
    medium: "mqdefault",
    high: "hqdefault",
    maxres: "maxresdefault",
  };

  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
}

export function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  }

  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function parseQueryString(query: string): Record<string, string> {
  const params = new URLSearchParams(query);
  const result: Record<string, string> = {};

  for (const [key, value] of params) {
    result[key] = value;
  }

  return result;
}

// ============================================================================
// ARRAY UTILITIES
// ============================================================================

export function groupBy<T, K extends string | number>(
  array: T[],
  keyFn: (item: T) => K
): Record<K, T[]> {
  return array.reduce(
    (acc, item) => {
      const key = keyFn(item);
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    },
    {} as Record<K, T[]>
  );
}

export function uniqueBy<T>(array: T[], keyFn: (item: T) => unknown): T[] {
  const seen = new Set();
  return array.filter((item) => {
    const key = keyFn(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function sortBy<T>(
  array: T[],
  keyFn: (item: T) => number | string,
  order: "asc" | "desc" = "asc"
): T[] {
  const sorted = [...array].sort((a, b) => {
    const aVal = keyFn(a);
    const bVal = keyFn(b);
    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
    return 0;
  });
  return order === "desc" ? sorted.reverse() : sorted;
}

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function isValidPhone(phone: string): boolean {
  const cleaned = phone.replace(/[\s\-\(\)]/g, "");
  return /^\+?[0-9]{9,15}$/.test(cleaned);
}

export function sanitizePhone(phone: string): string {
  return phone.replace(/[\s\-\(\)]/g, "");
}

// ============================================================================
// ID GENERATION
// ============================================================================

export function generateId(prefix: string = ""): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return prefix ? `${prefix}_${timestamp}${random}` : `${timestamp}${random}`;
}
